import { useMemo, useRef, useState } from "react";

import { AVAILABLE_LANGUAGES } from "../../../utils/constants";
import { MenuBottomCornersRounded, useSimpleMenuState } from "../../../common/components/Menu/Menu";
import { Link, LinkButton, LinkType } from "../../../common/components/Link";
import { IconType } from "../../../common/components/icon/IconType";
import { IconSize } from "../../../common/components/icon/IconSize";
import { Icon } from "../../../common/components/icon/Icon";
import { MENU_POS_LEFT } from "../../../common/components/Menu/MenuPositions";
import { MenuItem } from "../../../common/components/Menu/MenuItem";
import classNames from "classnames";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useTranslation } from "react-i18next";
import { LanguageService } from "./LanguageService";

interface Props {
  className?: string;
}

export const LanguageSelector = ({ className }: Props) => {
  const { currentLanguage, loading, languages, ...rest } = useLanguageSelector();
  const [isOpen, handleClick, handleClose, anchorEl, setAnchorEl] = useSimpleMenuState();

  const divRef = useRef<HTMLDivElement>(null);

  // Nothing to show if there is only 1 language
  if (languages.length <= 1) {
    return null;
  }

  const onClick = () => setAnchorEl(divRef.current);

  const onItemClick = (value: string) => {
    rest.onItemClick(value);
    handleClose();
  };

  const desktopItems = languages
    .filter((language) => language.value !== currentLanguage.value)
    .map(({ label, value }) => (
      <MenuItem key={value} onClick={() => onItemClick(value)}>
        {label}
        <span className={"scha__header__language__option"}> ({value})</span>
      </MenuItem>
    ));

  const topOffset = 24 + (48 - 20) / 2;

  return (
    <div
      className={classNames("scha__header__language", className, isOpen && "scha__header__language--isopen")}
      ref={divRef}
    >
      <LinkButton
        variant={LinkType.NAVIGATION}
        onClick={onClick}
        disabled={loading}
        data-test-id={"language-selector-dropdown"}
      >
        <span className={"scha__header__language__current"}>{currentLanguage.value}</span>
        <Icon
          className={"icon-margin-l scha__header__language--icon"}
          type={isOpen ? IconType.ChevronUp : IconType.ChevronDown}
          size={IconSize.FONT_20}
        />
      </LinkButton>
      <MenuBottomCornersRounded
        open={isOpen}
        anchorEl={anchorEl}
        {...MENU_POS_LEFT}
        onClose={handleClose}
        MenuListProps={{ disablePadding: true }}
        sx={{ marginTop: `${topOffset}px` }}
      >
        {desktopItems}
      </MenuBottomCornersRounded>
    </div>
  );
};

export const MobileLanguageSelector = ({ className }: Props) => {
  const { currentLanguage, loading, languages, onItemClick } = useLanguageSelector();
  const { val: isOpenMobile, setTrue: openLanguageMobile, setFalse: closeLanguageMobile } = useBooleanState(false);

  // Nothing to show if there is only 1 language
  if (languages.length <= 1) {
    return null;
  }

  const mobileItems = languages
    .filter((lang) => lang.value !== currentLanguage.value)
    .map(({ label, value }) => (
      <div
        className={"scha__header__language__dropdown-mobile-option"}
        key={value}
        onClick={() => {
          onItemClick(value);
          closeLanguageMobile();
        }}
      >
        {label}
      </div>
    ));

  return (
    <>
      <Link
        variant={LinkType.NAVIGATION}
        onClick={isOpenMobile ? closeLanguageMobile : openLanguageMobile}
        disabled={loading}
        data-test-id={"language-selector-dropdown"}
        className={"scha__header__language__dropdown-mobile"}
      >
        <span className={"scha__header__language__current"}>{currentLanguage.label}</span>
        <Icon
          className={"icon-margin-l scha__header__language--icon"}
          type={isOpenMobile ? IconType.ChevronUp : IconType.ChevronDown}
          size={IconSize.FONT_20}
        />
      </Link>
      {isOpenMobile && mobileItems}
    </>
  );
};

const useAvailableLanguages = () => {
  const { t } = useTranslation();
  return useMemo(
    () =>
      AVAILABLE_LANGUAGES.map((key) => ({
        label: t(`language.${key}`),
        value: key,
      })).sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );
};

export const useLanguageSelector = () => {
  const [currLngCode, setCurrLngCode] = useState(LanguageService.getCurrentLanguage);
  const [disabled, setDisabled] = useState(false);

  const languages = useAvailableLanguages();

  const onItemClick = (code: string) => {
    setCurrLngCode(code);
    setDisabled(true);
    LanguageService.changeLanguage(code);
  };

  const currentLanguage = languages.find(({ value }) => value === currLngCode) ?? { label: "", value: "" };

  return {
    currentLanguage,
    loading: disabled,
    languages,
    onItemClick,
  };
};
